import { Accordion, Button, Card, Table, Alert, Image, Row, Col, Form, FormGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import UseFormatDate from "../../hooks/UseFormatDate";
import ProjectService from "../../services/project.service";

import APP_CONF from "../../scripts/constants";
import ProjectForm from "../../components/forms/ProjectForm";
import PaginationComponent from "../../components/PaginationComponent";
import TableHeaderSortComponent from "../../components/TableHeaderSortComponent";
import UserPermissionService from "../../services/userPermission.service";

import UseAlertConfirm from "../../hooks/UseAlertConfirm";

const Project = () =>{

    const { t, i18n } = useTranslation();

    const language = i18n.language.split('-')[0];

    const { showModal } = UseAlertConfirm();

    const { register, handleSubmit } = useForm({
        mode: "all",
    });

    const [sortColumn, setSortColumn] = useState({
        sortByColumn: null,
        orderBy: null
    });

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalItems: 0,
        itemsPerPage: APP_CONF.ITEMS_PER_PAGE,
        numPages: 1
    });
    const [filter, setFilter] = useState({
        active: ""
    })

    const [success, setSuccess] = useState({
        create: false,
        delete: false,
    });
    const [errors, setErrors] = useState({
        create: false,
        delete: false,
    });

    const [projects, setProjects] = useState([]);

    const [indexDesc, setIndexDesc] = useState(false);

    const [permissions, setPermissions] = useState({});

    const getPermissions = async () => {
        try {
            const data = await UserPermissionService.getPermissionsByConnected();
            setPermissions(data);
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        const loadData = async () => {
            await getPermissions();
        }

        loadData();
    }, []);

    useEffect(() => {
        getProjects();
        if (sortColumn.sortByColumn === 'id' && sortColumn.orderBy === 'desc') {
            setIndexDesc(true);
        } else {
            setIndexDesc(false);
        }
    }, [pagination.currentPage, filter, sortColumn]);

    const getProjects = async () => {
        try {
            let languageToSearch;

            const sortColumnsByLanguage = ['title']

            if (sortColumnsByLanguage.includes(sortColumn.sortByColumn) &&
                (sortColumn.orderBy !== null || sortColumn.orderBy !== undefined)) {
                languageToSearch = language;
            }

            const data = await ProjectService.getAll(pagination, sortColumn, languageToSearch, filter)
            setProjects(data.content);
            setPagination({
                ...pagination,
                totalItems: data.totalElements,
                numPages: data.totalPages
            });
        } catch ({ response }) {
            console.error(response);
        }
    }

    const createProject = async (data) => {
        setErrors({ ...errors, create: false });
        setSuccess({ ...success, create: false });

        try {
            await ProjectService.create(data);
            setSuccess({ ...success, create: true });
            getProjects();
        } catch (e) {
            console.error(e.response);
            setErrors({ ...errors, create: true });
        }
    };

    const deleteProject = async (id) => {

        showModal({
            title: t("general.delete.title"),
            body: t("general.delete.message"),
            confirmButtonVariant: "danger",
            onConfirm: async () => {
                setErrors({ ...errors, create: false });
                setSuccess({ ...success, create: false });
                try {
                    await ProjectService.remove(id);
                    setSuccess({ ...success, delete: true });
                    getProjects();
                } catch (e) {
                    console.error(e.response);
                    setErrors({ ...errors, delete: true });
                }
            }
        });

    }

    const filterSearch = (data) => {
        let obj = data;
        if (obj.search === "") {
            obj = {
                active: data.active
            }
        }
        setFilter(obj);
    }

    return (
        <>
            <div className={"d-sm-flex align-items-center justify-content-between mb-4"}>
                <h1 className={"h3 mb-0 text-gray-800"}>{t('projects.title')}</h1>
            </div>

            <Alert variant={"success"} show={success.create} dismissible onClose={() => setSuccess({ ...success, create: false })}>
                {t('projects.success.create')}
            </Alert>

            <Alert variant={"danger"} show={errors.create} dismissible onClose={() => setErrors({ ...errors, create: false })}>
                {t('projects.error.create')}
            </Alert>

            <Alert variant={"success"} show={success.delete} dismissible onClose={() => setSuccess({ ...success, delete: false })}>
                {t('projects.success.delete')}
            </Alert>

            <Alert variant={"danger"} show={errors.delete} dismissible onClose={() => setErrors({ ...errors, delete: false })}>
                {t('projects.error.delete')}
            </Alert>

            {permissions.projects?.read && permissions.projects.create ? 
            <Card className={"shadow mb-4"}>
                <Accordion>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header><h6 className={"m-0 fw-bold text-primary"}>{t('projects.creation.title')}</h6>
                        </Accordion.Header>
                        <Accordion.Body>
                            <ProjectForm onSubmit={createProject} permissions={permissions}/>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Card>
            : null }

            <Card className={"shadow mb-4"}>
                <Card.Header className={"py-3"}>
                    <h6 className={"m-0 fw-bold text-primary"}>{t('projects.list.title')}</h6>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit(filterSearch)}>
                        <Row>
                            <Col lg="2" className={"mb-3"}>
                                <FormGroup>
                                    <Form.Label>{t('projects.filter.active.title')}</Form.Label>
                                    <Form.Select {...register('active')} aria-label="Default select example">
                                        <option value="">{t('projects.filter.active.value1')}</option>
                                        <option value={true}>{t('projects.filter.active.value2')}</option>
                                        <option value={false}>{t('projects.filter.active.value3')}</option>
                                    </Form.Select>
                                </FormGroup>
                            </Col>
                            <Col lg="3" className={"mb-3"}>
                                <FormGroup>
                                    <Form.Label>{t('projects.filter.search')}</Form.Label>
                                    <Form.Control type="text" {...register("search")} />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Button type="submit" className={"mt-3"} disabled={!permissions.projects?.read}>{t('projects.filter.button')}</Button>
                    </Form>
                    <hr />
                    <Table responsive>
                        <thead>
                        <tr>
                            <TableHeaderSortComponent scope={"col"} text={t('projects.list.fields.index')} column={"id"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                            <TableHeaderSortComponent scope={"col"} text={t('projects.list.fields.title')} column={"title"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                            <TableHeaderSortComponent scope={"col"} text={t('projects.list.fields.creationDate')} column={"creationDate"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                            <TableHeaderSortComponent scope={"col"} text={t('projects.list.fields.active')} column={"active"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {projects.length > 0 ? projects.map((project, index) => (
                                <tr key={index}>
                                    {indexDesc ? <td>{projects.length - index}</td> : <td>{index + 1}</td>}
                                    <td>
                                        <div className="d-flex flex-row flex-nowrap">
                                            {Object.values(project.title).map((translation, i) =>
                                                <div key={i} className="me-2">
                                                    <Image src={translation.language.icon} className="me-2 flags-img-sm" />
                                                    {i + 1 === Object.values(project.title).length ? translation.value : translation.value + ' | '}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>{UseFormatDate(project.creationDate)}</td>
                                    <td>{project.active ? 'Si' : 'No'}</td>
                                    <td className={"text-center"}>
                                        <Button variant="link" size="sm" className="ms-2" to={`/project/${project.id}`} as={Link} disabled={!permissions.projects?.read}>
                                            <FontAwesomeIcon icon="edit" />
                                        </Button>
                                        <Button variant="link" size="sm" className="text-danger" onClick={() => deleteProject(project.id)} disabled={!permissions.projects?.delete}>
                                            <FontAwesomeIcon icon="trash" />
                                        </Button>
                                    </td>
                                </tr>
                            )) :
                            <tr>
                                <td colSpan={4}>{t('general.empty')}</td>
                            </tr>
                        }
                        </tbody>
                    </Table>
                    <PaginationComponent pagination={pagination}
                                         setPagination={setPagination}
                                         alwaysShown={projects.length > 0} />
                </Card.Body>
            </Card>
        </>
    );
}

export default Project;